import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { useLanguage } from './LanguageContext';
import { TrendingUp, CheckCircle2 } from 'lucide-react';
interface SlideFinancialProjectionsProps {
  onContinue: () => void;
}
// Numbers from OVERSIGT 2026–2029 (DKK) — 2025 removed
const YEARS = [{
  year: '2026',
  label: 'Budget',
  revenue: 5.82,
  grossMargin: 4.37,
  netResult: -1.39
}, {
  year: '2027',
  label: 'Budget',
  revenue: 12.82,
  grossMargin: 9.62,
  netResult: -1.51
}, {
  year: '2028',
  label: 'Budget',
  revenue: 22.75,
  grossMargin: 18.2,
  netResult: 5.45,
  profitable: true
}, {
  year: '2029',
  label: 'Budget',
  revenue: 36.0,
  grossMargin: 30.6,
  netResult: 18.6,
  profitable: true
}];
export function SlideFinancialProjections({
  onContinue
}: SlideFinancialProjectionsProps) {
  const {
    t
  } = useLanguage();
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 250);
    const t2 = setTimeout(() => setStage(2), 700);
    const t3 = setTimeout(() => setStage(3), 1300);
    const t4 = setTimeout(() => setStage(4), 2200);
    return () => {
      ;
      [t1, t2, t3, t4].forEach(clearTimeout);
    };
  }, []);
  // Chart geometry
  const chartW = 720;
  const chartH = 300;
  const padL = 50;
  const padR = 28;
  const padT = 30;
  const padB = 36;
  const innerW = chartW - padL - padR;
  const innerH = chartH - padT - padB;
  const yMin = -5;
  const yMax = 40;
  const yToPx = (v: number) => padT + innerH - (v - yMin) / (yMax - yMin) * innerH;
  const colXs = YEARS.map((_, i) => padL + innerW * (i + 0.5) / YEARS.length);
  const barWidth = innerW / YEARS.length * 0.5;
  const zeroY = yToPx(0);
  const yTicks = [0, 10, 20, 30, 40];
  // Net result line path
  const netLinePath = YEARS.map((y, i) => `${i === 0 ? 'M' : 'L'} ${colXs[i]} ${yToPx(y.netResult)}`).join(' ');
  return <div className="h-full w-full flex flex-col px-8 py-7 md:px-16 md:py-9 max-w-[1400px] mx-auto" data-id="element-442">
      {/* Heading */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-center space-y-4" data-id="element-443">
        <h2 className="text-3xl md:text-5xl font-sans font-bold text-white tracking-tight leading-tight" data-id="element-444">
          {t('Financial Projections', 'Finansielle fremskrivninger')}
        </h2>
        <p className="text-teal-400 text-base md:text-lg font-medium mt-1" data-id="element-445">
          {t('2026 – 2029 · Path to scale', '2026 – 2029 · Vejen til skalering')}
        </p>
      </motion.div>

      {/* Stat tiles */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 1 ? 1 : 0,
      y: stage >= 1 ? 0 : 20
    }} className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-id="element-446">
        {[{
        label: t('2026 Budget Revenue', '2026 Budget Omsætning'),
        value: 'DKK 5.82M',
        sub: t('Starting point', 'Udgangspunkt'),
        tone: 'navy'
      }, {
        label: t('2027 Budget Revenue', '2027 Budget Omsætning'),
        value: 'DKK 12.82M',
        sub: t('2.2× vs 2026', '2,2× ift. 2026'),
        tone: 'dark'
      }, {
        label: t('Profitable from', 'Overskudsgivende fra'),
        value: '2028',
        sub: t('Net result +DKK 5.45M', 'Nettoresultat +DKK 5,45M'),
        tone: 'green',
        icon: <CheckCircle2 className="w-4 h-4" data-id="element-447" />
      }, {
        label: t('2029 Revenue Target', '2029 Omsætningsmål'),
        value: 'DKK 36M',
        sub: t('Net result +DKK 18.6M', 'Nettoresultat +DKK 18,6M'),
        tone: 'teal',
        icon: <TrendingUp className="w-4 h-4" data-id="element-448" />
      }].map((s, i) => {
        const isTeal = s.tone === 'teal';
        const isGreen = s.tone === 'green';
        const isDark = s.tone === 'dark';
        return <div key={s.label} className={`rounded-xl p-4 border ${isTeal ? 'bg-teal-400/10 border-teal-400/40' : isGreen ? 'bg-emerald-500/5 border-emerald-400/30' : isDark ? 'bg-navy-950 border-navy-700' : 'bg-navy-900/60 border-navy-700'}`} data-id="element-449">
              <div className="flex items-center gap-1.5" data-id="element-450">
                {s.icon && <span className={isTeal ? 'text-teal-400' : isGreen ? 'text-emerald-400' : 'text-white/45'} data-id="element-451">
                    {s.icon}
                  </span>}
                <p className="text-white/45 text-[9px] uppercase tracking-widest font-bold" data-id="element-452">
                  {s.label}
                </p>
              </div>
              <p className={`text-xl md:text-2xl font-bold tabular-nums mt-1 ${isTeal ? 'text-teal-400' : isGreen ? 'text-emerald-400' : 'text-white'}`} data-id="element-453">
                {s.value}
              </p>
              <p className={`text-[10px] mt-0.5 ${isTeal ? 'text-teal-300/80' : isGreen ? 'text-emerald-300/80' : 'text-white/50'}`} data-id="element-454">
                {s.sub}
              </p>
            </div>;
      })}
      </motion.div>

      {/* Chart card */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 2 ? 1 : 0,
      y: stage >= 2 ? 0 : 20
    }} transition={{
      duration: 0.5
    }} className="flex-1 mt-4 bg-navy-900/60 border border-teal-400/20 rounded-2xl p-5 md:p-6 flex flex-col min-h-0" data-id="element-455">
        <div className="flex items-center justify-between pb-3 border-b border-teal-400/20" data-id="element-456">
          <div className="flex items-center gap-2" data-id="element-457">
            <div className="w-6 h-6 rounded-md bg-teal-400 flex items-center justify-center" data-id="element-458">
              <div className="w-2.5 h-2.5 rounded-full bg-white" data-id="element-459" />
            </div>
            <span className="text-teal-400 font-bold text-sm" data-id="element-460">snakker</span>
          </div>
          <h3 className="text-sm md:text-base font-bold text-white" data-id="element-461">
            4-Year <span className="text-teal-400" data-id="element-462">Revenue</span> & Net Result
          </h3>
          <div className="flex items-center gap-3 text-[10px]" data-id="element-463">
            <div className="flex items-center gap-1.5" data-id="element-464">
              <div className="w-2.5 h-2.5 rounded-sm bg-teal-400" data-id="element-465" />
              <span className="text-white/70" data-id="element-466">Revenue (DKK M)</span>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-467">
              <div className="w-2.5 h-2.5 rounded-sm bg-amber-400" data-id="element-468" />
              <span className="text-white/70" data-id="element-469">Net Result</span>
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 mt-2" data-id="element-470">
          <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet" data-id="element-471">
            <defs data-id="element-472">
              <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-473">
                <stop offset="0%" stopColor="#00D4AA" stopOpacity="0.95" data-id="element-474" />
                <stop offset="100%" stopColor="#00D4AA" stopOpacity="0.55" data-id="element-475" />
              </linearGradient>
            </defs>

            {/* Y grid + labels */}
            {yTicks.map(t => <g key={t} data-id="element-476">
                <line x1={padL} y1={yToPx(t)} x2={chartW - padR} y2={yToPx(t)} stroke="#cbd5e1" strokeOpacity={t === 0 ? 0.6 : 0.25} strokeDasharray={t === 0 ? '0' : '3 4'} strokeWidth="0.7" data-id="element-477" />
                <text x={padL - 8} y={yToPx(t) + 3} textAnchor="end" className="fill-white/45 text-[10px]" data-id="element-478">
                  {t}M
                </text>
              </g>)}

            {/* Zero baseline emphasized */}
            <line x1={padL} y1={zeroY} x2={chartW - padR} y2={zeroY} stroke="#cbd5e1" strokeOpacity="0.7" strokeWidth="1" data-id="element-479" />

            {/* Revenue bars */}
            {YEARS.map((y, i) => {
            const top = yToPx(y.revenue);
            const h = zeroY - top;
            return <motion.rect key={y.year} x={colXs[i] - barWidth / 2} y={top} width={barWidth} height={h} rx={4} fill="url(#revGrad)" initial={{
              opacity: 0,
              scaleY: 0
            }} animate={{
              opacity: stage >= 3 ? 1 : 0,
              scaleY: stage >= 3 ? 1 : 0
            }} transition={{
              duration: 0.6,
              delay: 0.1 + i * 0.12,
              ease: 'easeOut'
            }} style={{
              transformOrigin: `${colXs[i]}px ${zeroY}px`
            }} data-id="element-480" />;
          })}

            {/* Revenue value labels above bars */}
            {YEARS.map((y, i) => <motion.text key={`rev-${y.year}`} x={colXs[i]} y={yToPx(y.revenue) - 8} textAnchor="middle" className="fill-teal-300 text-[10px] font-bold tabular-nums" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            delay: 0.2 + i * 0.08
          }} data-id="element-481">
                {y.revenue}M
              </motion.text>)}

            {/* Net result line */}
            <motion.path d={netLinePath} fill="none" stroke="#FBBF24" strokeWidth="2.4" strokeLinecap="round" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 1.6,
            ease: 'easeInOut',
            delay: 0.3
          }} data-id="element-482" />

            {/* Net result dots */}
            {YEARS.map((y, i) => <motion.g key={`net-${y.year}`} initial={{
            opacity: 0,
            scale: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0,
            scale: stage >= 4 ? 1 : 0
          }} transition={{
            delay: 0.6 + i * 0.1,
            type: 'spring',
            stiffness: 240
          }} data-id="element-483">
                <circle cx={colXs[i]} cy={yToPx(y.netResult)} r={4} fill={y.netResult >= 0 ? '#FBBF24' : '#94a3b8'} stroke="#0b1426" strokeWidth="2" data-id="element-484" />
                <text x={colXs[i]} y={yToPx(y.netResult) + (y.netResult >= 0 ? -10 : 16)} textAnchor="middle" className={`text-[9px] font-bold tabular-nums ${y.netResult >= 0 ? 'fill-amber-300' : 'fill-white/60'}`} data-id="element-485">
                  {y.netResult > 0 ? '+' : ''}
                  {y.netResult}M
                </text>
              </motion.g>)}

            {/* Profitability inflection marker between 2027 & 2028 */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            delay: 1.4
          }} data-id="element-486">
              <line x1={(colXs[1] + colXs[2]) / 2} y1={padT} x2={(colXs[1] + colXs[2]) / 2} y2={chartH - padB} stroke="#10b981" strokeOpacity="0.45" strokeDasharray="4 4" strokeWidth="1" data-id="element-487" />
              <rect x={(colXs[1] + colXs[2]) / 2 - 38} y={padT - 2} width="76" height="18" rx="9" fill="#10b981" fillOpacity="0.18" stroke="#10b981" strokeOpacity="0.5" data-id="element-488" />
              <text x={(colXs[1] + colXs[2]) / 2} y={padT + 10} textAnchor="middle" className="fill-emerald-300 text-[9px] font-bold uppercase tracking-wider" data-id="element-489">
                {t('Profitable', 'Overskudsgivende')}
              </text>
            </motion.g>

            {/* X labels — year + draft/budget */}
            {YEARS.map((y, i) => <g key={`x-${y.year}`} data-id="element-490">
                <text x={colXs[i]} y={chartH - padB + 16} textAnchor="middle" className="fill-white/85 text-[11px] font-bold" data-id="element-491">
                  {y.year}
                </text>
                <text x={colXs[i]} y={chartH - padB + 28} textAnchor="middle" className="fill-white/40 text-[9px] uppercase tracking-wider" data-id="element-492">
                  {y.label}
                </text>
              </g>)}
          </svg>
        </div>
      </motion.div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 5 ? 1 : 0
    }} className="mt-8 flex justify-end" data-id="element-493">
        <Button onClick={onContinue} data-id="element-494">
          {t('The Raise', 'Kapitalrejsningen')}
        </Button>
      </motion.div>
    </div>;
}